import {
  LockOutlined, LogoutOutlined, SettingOutlined, UserOutlined
} from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alert, Avatar, Button, Descriptions, Dropdown, Form, Input, Modal, Radio, Select, Space, Tag, message
} from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authApi } from "../auth/auth-api";
import { useAuth } from "../auth/AuthProvider";
import { useAppConfig } from "../../shared/config/AppConfigProvider";

type PasswordValues = { currentPassword: string; newPassword: string; confirmPassword: string };
type SettingsValues = { locale: "en" | "zh"; theme: "light" | "dark" };

export function AccountMenu() {
  const { user,logout } = useAuth();
  const { text,locale,setLocale,theme,setTheme } = useAppConfig();
  const navigate = useNavigate();
  const [accountOpen,setAccountOpen] = useState(false);
  const [passwordOpen,setPasswordOpen] = useState(false);
  const [settingsOpen,setSettingsOpen] = useState(false);
  const [passwordForm] = Form.useForm<PasswordValues>();
  const [settingsForm] = Form.useForm<SettingsValues>();
  const account = useQuery({ queryKey:["current-account"],queryFn:authApi.me,enabled:accountOpen });
  const changePassword = useMutation({
    mutationFn: authApi.changePassword,
    onSuccess: () => {
      message.success(text.passwordChanged);
      setPasswordOpen(false);
      passwordForm.resetFields();
      logout();
      navigate("/login",{replace:true});
    }
  });
  const doLogout = () => {
    const refreshToken = localStorage.getItem("autoflow.refreshToken");
    const finish = () => { logout(); navigate("/login",{replace:true}); };
    if (refreshToken) void authApi.logout(refreshToken).finally(finish); else finish();
  };
  const openSettings = () => {
    settingsForm.setFieldsValue({locale,theme});
    setSettingsOpen(true);
  };
  const saveSettings = async () => {
    const values = await settingsForm.validateFields();
    setLocale(values.locale);
    setTheme(values.theme);
    setSettingsOpen(false);
  };
  const currentUser = account.data ?? user;
  const menuItems = [
    {key:"account",icon:<UserOutlined/>,label:text.currentAccount},
    {key:"password",icon:<LockOutlined/>,label:text.changePassword},
    {key:"settings",icon:<SettingOutlined/>,label:text.personalSettings},
    {type:"divider" as const},
    {key:"logout",icon:<LogoutOutlined/>,label:text.signOut,danger:true}
  ];
  return <>
    <Dropdown trigger={["click"]} menu={{items:menuItems,onClick:({key})=>{
      if(key==="account") setAccountOpen(true);
      if(key==="password") { changePassword.reset(); setPasswordOpen(true); }
      if(key==="settings") openSettings();
      if(key==="logout") doLogout();
    }}}>
      <Button type="text"><Avatar size="small">{user?.displayName.slice(0,2).toUpperCase()}</Avatar><span className="desktop-only">{user?.displayName}</span></Button>
    </Dropdown>

    <Modal open={accountOpen} onCancel={()=>setAccountOpen(false)} footer={null} title={text.currentAccount}>
      {account.isError&&<Alert type="error" showIcon message={(account.error as Error).message}/>} 
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label={text.displayName}>{currentUser?.displayName}</Descriptions.Item>
        <Descriptions.Item label={text.email}>{currentUser?.email}</Descriptions.Item>
        <Descriptions.Item label={text.roles}><Space wrap>{currentUser?.roles.map(role=><Tag key={role} color="blue">{role}</Tag>)}</Space></Descriptions.Item>
      </Descriptions>
    </Modal>

    <Modal
      open={passwordOpen}
      onCancel={()=>setPasswordOpen(false)}
      title={text.changePassword}
      okText={text.changePassword}
      confirmLoading={changePassword.isPending}
      onOk={()=>void passwordForm.validateFields().then(values=>changePassword.mutate({
        currentPassword:values.currentPassword,newPassword:values.newPassword
      }))}
    >
      <Alert type="info" showIcon message={text.passwordChangeHint} style={{marginBottom:16}}/>
      {changePassword.isError&&<Alert type="error" showIcon message={(changePassword.error as Error).message} style={{marginBottom:16}}/>}
      <Form form={passwordForm} layout="vertical" preserve={false}>
        <Form.Item name="currentPassword" label={text.currentPassword} rules={[{required:true},{min:8}]}>
          <Input.Password autoComplete="current-password"/>
        </Form.Item>
        <Form.Item name="newPassword" label={text.newPassword} rules={[{required:true},{min:12}]}>
          <Input.Password autoComplete="new-password"/>
        </Form.Item>
        <Form.Item name="confirmPassword" label={text.confirmNewPassword} dependencies={["newPassword"]} rules={[
          {required:true},
          ({getFieldValue})=>({validator:(_,value)=>!value||getFieldValue("newPassword")===value?Promise.resolve():Promise.reject(new Error(text.passwordsDoNotMatch))})
        ]}>
          <Input.Password autoComplete="new-password"/>
        </Form.Item>
      </Form>
    </Modal>

    <Modal open={settingsOpen} onCancel={()=>setSettingsOpen(false)} onOk={()=>void saveSettings()} title={text.personalSettings} okText={text.save}>
      <Form form={settingsForm} layout="vertical">
        <Form.Item name="locale" label={text.language} rules={[{required:true}]}>
          <Select options={[{value:"en",label:"English"},{value:"zh",label:"中文"}]}/>
        </Form.Item>
        <Form.Item name="theme" label={text.theme} rules={[{required:true}]}>
          <Radio.Group options={[{value:"light",label:text.light},{value:"dark",label:text.dark}]}/>
        </Form.Item>
      </Form>
    </Modal>
  </>;
}
